import { Search, School, Add } from '@mui/icons-material';
import { 
  Box, Button, InputAdornment, List, ListItemButton, 
  ListItemText, Paper, TextField, Typography, Pagination, 
  ListItem,
  CircularProgress,
  Tooltip
} from '@mui/material';

interface SidebarProps {
  state: any; // Dica: Você pode tipar usando ReturnType<typeof useSchoolClass>['state']
  actions: any; // Dica: Você pode tipar usando ReturnType<typeof useSchoolClass>['actions']
}

export function SchoolClassSidebar({ state, actions }: SidebarProps) {
  // Desestruturando o que precisamos do hook para deixar o código limpo
  const { selectedId, classList, totalCount, pagination, isLoadingList, searchTerm } = state;
  const { handleSelectClass, handleCreateNew, setSearchTerm, handleChangePage } = actions;

  // Calculando o total de páginas com base no pageSize configurado no hook (ex: 10)
  const pageCount = Math.ceil(totalCount / pagination.pageSize) || 1;

  return (
    <Paper
      component="nav"
      aria-label="Navegação de turmas"
      elevation={0}
      sx={{
        width: { xs: '100%', md: 320 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* HEADER */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Turmas
        </Typography>
        {/* ✅ AÇÃO: Botão Nova Turma agora chama o handler correto */}
        <Tooltip title="Cadastrar nova turma" placement="bottom" arrow>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ minWidth: 36, p: 0.5, height: 36 }} 
            onClick={handleCreateNew} 
          >
            <Add fontSize="small" />
          </Button>
        </Tooltip>
      </Box>

      {/* BUSCA */}
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar turma..."
          value={searchTerm} // ✅ ESTADO: Vinculado ao termo de busca
          onChange={(e) => setSearchTerm(e.target.value)} // ✅ AÇÃO: Atualiza a busca (com debounce no hook)
          slotProps={{
            input: {
              'aria-label': 'Buscar turma',
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              sx: { bgcolor: 'background.default' }
            }
          }}
        />
      </Box>

      {/* ITEM ESPECIAL: TODAS AS TURMAS */}
      <List disablePadding sx={{ px: 2, pb: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleSelectClass('all')} // ✅ AÇÃO: Seleciona o Dashboard Global
            sx={{
              borderRadius: 2,
              bgcolor: selectedId === 'all' ? 'divider' : 'transparent',
              '&:hover': { bgcolor: 'divider' },
            }}
          >
            <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
              <School fontSize="small" color="primary" />
            </Box>
            <Typography variant="body2" fontWeight="medium" color="text.primary">
              Todas as Turmas
            </Typography>
          </ListItemButton>
        </ListItem>
      </List>

      <Box sx={{ mx: 2, my: 1, borderTop: '1px solid', borderColor: 'divider' }} />

      {/* LISTA DE TURMAS (Integração com a API) */}
      <List sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
        {isLoadingList ? (
          // ✅ Feedback visual não intrusivo para carregamento
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : classList.length === 0 ? (
          // ✅ Tratamento caso a busca não retorne nada ou a licença não tenha turmas
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
            Nenhuma turma encontrada.
          </Typography>
        ) : (
          // ✅ Mapeando os dados reais da API (ISchoolClassSidebarProps)
          classList.map((cls: any) => (
            <ListItem key={cls.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleSelectClass(cls.id)} // ✅ AÇÃO: Passa o ID real (number)
                sx={{
                  borderRadius: 2,
                  borderLeft: '4px solid',
                  borderColor: selectedId === cls.id ? 'primary.main' : 'transparent',
                  bgcolor: selectedId === cls.id ? 'divider' : 'transparent',
                  '&:hover': { bgcolor: 'divider' },
                }}
              >
                <ListItemText
                  primary={cls.name}
                  secondary={`${cls.studentCount || 0} alunos`} // ✅ Exibindo a contagem real que vem do C#
                  slotProps={{
                    primary: { variant: 'body2', fontWeight: selectedId === cls.id ? 'bold' : 'medium', color: selectedId === cls.id ? 'text.primary' : 'text.secondary', noWrap: true },
                    secondary: { variant: 'caption', color: 'text.secondary', noWrap: true }
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>

      {/* RODAPÉ COM PAGINAÇÃO */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center', mt: 'auto' }}>
        <Pagination 
          count={pageCount} // ✅ ESTADO: Quantidade de páginas dinâmicas
          page={pagination.pageNumber} // ✅ ESTADO: Página atual
          onChange={handleChangePage} // ✅ AÇÃO: Dispara a troca de página no hook
          color="primary" 
          size="small" 
          siblingCount={0}
          boundaryCount={1}
        />
      </Box>
    </Paper>
  );
}